import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DataTable from './DataTable';
import DepartmentSelect from './DepartmentSelect';
import SearchableSelect from './SearchableSelect';
import Loader from './Loader';
import ExternalEmployeeSelect from './ExternalEmployeeSelect';
import EmployeeBulkOperations from './EmployeeBulkOperations';
import Alert from './Alert';
import { useAlert } from '../hooks/useAlert';
import { dataCache } from '../utils/dataCache';

const API_URL = '/api';

function Employees() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    usl: '',
    mobile: '',
    department: '',
    discomName: '',
    zone: '',
    circle: '',
    division: ''
  });
  const [discomNames, setDiscomNames] = useState([]);
  const [zones, setZones] = useState([]);
  const [circles, setCircles] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showAssignmentsModal, setShowAssignmentsModal] = useState(false);
  const [selectedEmployeeAssignments, setSelectedEmployeeAssignments] = useState([]);
  const [assetFilter, setAssetFilter] = useState('all');
  const { alert, showAlert, hideAlert } = useAlert();

  useEffect(() => {
    fetchEmployees();
    fetchDiscomNames();
    document.title = 'Employees - Saral Asset Manager';
  }, []);

  const getUniqueActiveAssignmentsForEmployee = (assignments, employeeId) => {
    const employeeAssignments = assignments
      .filter(a => a.employeeId === employeeId && a.isActive)
      .sort((a, b) => new Date(b.assignedDate) - new Date(a.assignedDate));

    const seenAssetIds = new Set();
    const uniqueAssignments = [];

    for (const assignment of employeeAssignments) {
      const assetKey = assignment.assetId ?? assignment.saralAssetId ?? assignment.id;
      if (seenAssetIds.has(assetKey)) continue;
      seenAssetIds.add(assetKey);
      uniqueAssignments.push(assignment);
    }

    return uniqueAssignments;
  };

  const fetchDiscomNames = async () => {
    try {
      const response = await axios.get(`${API_URL}/discoms/discom-names`);
      setDiscomNames(response.data);
    } catch (error) {
      console.error('Error fetching discom names:', error);
    }
  };

  const fetchZones = async (discomName) => {
    try {
      const response = await axios.get(`${API_URL}/discoms/zones/${discomName}`);
      setZones(response.data);
    } catch (error) {
      console.error('Error fetching zones:', error);
    }
  };

  const fetchCircles = async (discomName, zone) => {
    try {
      const response = await axios.get(`${API_URL}/discoms/circles/${discomName}/${zone}`);
      setCircles(response.data);
    } catch (error) {
      console.error('Error fetching circles:', error);
    }
  };

  const fetchDivisions = async (discomName, zone, circle) => {
    try {
      const response = await axios.get(`${API_URL}/discoms/divisions/${discomName}/${zone}/${circle}`);
      setDivisions(response.data);
    } catch (error) {
      console.error('Error fetching divisions:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const cached = dataCache.get('employees');
      if (cached) {
        setEmployees(cached);
        setLoading(false);
        return;
      }

      const response = await axios.get(`${API_URL}/employees`);
      const assignmentsResponse = await axios.get(`${API_URL}/assetassignments`);
      
      const employeesWithAssets = response.data.map(emp => ({
        ...emp,
        assetCount: getUniqueActiveAssignmentsForEmployee(assignmentsResponse.data, emp.id).length
      }));
      
      dataCache.set('employees', employeesWithAssets);
      setEmployees(employeesWithAssets);
    } catch (error) {
      console.error('Error fetching employees:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Find the discom ID based on the selected values
      const discomResponse = await axios.get(`${API_URL}/discoms`);
      const selectedDiscom = discomResponse.data.find(d => 
        d.discomName === formData.discomName &&
        d.zone === formData.zone &&
        d.circle === formData.circle &&
        d.division === formData.division
      );
      
      const submitData = {
        name: formData.name,
        USL: formData.usl,
        mobile: formData.mobile,
        department: formData.department,
        discomId: selectedDiscom?.id || null
      };
      
      if (editingId) {
        await axios.put(`${API_URL}/employees/${editingId}`, submitData);
        handleCloseModal();
        showAlert('success', 'Employee updated successfully!');
      } else {
        await axios.post(`${API_URL}/employees`, submitData);
        handleCloseModal();
        showAlert('success', 'Employee added successfully!');
      }
      dataCache.clear('employees');
      fetchEmployees();
    } catch (error) {
      console.error('Error saving employee:', error);
      const errorMsg = error.response?.data || error.message || 'Failed to save employee';
      showAlert('danger', `Error: ${errorMsg}`);
    }
  };

  const handleAdd = () => {
    setFormData({ name: '', usl: '', mobile: '', department: '', discomName: '', zone: '', circle: '', division: '' });
    setEditingId(null);
    setZones([]);
    setCircles([]);
    setDivisions([]);
    setShowModal(true);
  };

  const handleEdit = (employee) => {
    setFormData({
      name: employee.name,
      usl: employee.usl || '',
      mobile: employee.mobile,
      department: employee.department,
      discomName: employee.discom?.discomName || '',
      zone: employee.discom?.zone || '',
      circle: employee.discom?.circle || '',
      division: employee.discom?.division || ''
    });
    setEditingId(employee.id);
    
    // Load cascading data if discom is selected
    if (employee.discom?.discomName) {
      fetchZones(employee.discom.discomName);
      if (employee.discom.zone) {
        fetchCircles(employee.discom.discomName, employee.discom.zone);
        if (employee.discom.circle) {
          fetchDivisions(employee.discom.discomName, employee.discom.zone, employee.discom.circle);
        }
      }
    }
    
    setShowModal(true);
  };

  const handleDelete = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${API_URL}/employees/${deleteId}`);
      setShowDeleteModal(false);
      setDeleteId(null);
      showAlert('success', 'Employee deleted successfully!');
      dataCache.clear('employees');
      fetchEmployees();
    } catch (error) {
      console.error('Error deleting employee:', error);
      const errorMsg = error.response?.data || error.message || 'Failed to delete employee';
      showAlert('danger', `Error: ${errorMsg}`);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setFormData({ name: '', usl: '', mobile: '', department: '', discomName: '', zone: '', circle: '', division: '' });
    setEditingId(null);
    setZones([]);
    setCircles([]);
    setDivisions([]);
  };

  const handleDiscomChange = (value) => {
    const discomName = value?.target ? value.target.value : value;
    setFormData({ ...formData, discomName, zone: '', circle: '', division: '' });
    setZones([]);
    setCircles([]);
    setDivisions([]);
    if (discomName) {
      fetchZones(discomName);
    }
  };

  const handleZoneChange = (value) => {
    const zone = value?.target ? value.target.value : value;
    setFormData({ ...formData, zone, circle: '', division: '' });
    setCircles([]);
    setDivisions([]);
    if (zone && formData.discomName) {
      fetchCircles(formData.discomName, zone);
    }
  };

  const handleCircleChange = (value) => {
    const circle = value?.target ? value.target.value : value;
    setFormData({ ...formData, circle, division: '' });
    setDivisions([]);
    if (circle && formData.discomName && formData.zone) {
      fetchDivisions(formData.discomName, formData.zone, circle);
    }
  };

  const handleViewAssignments = async (employee) => {
    try {
      const response = await axios.get(`${API_URL}/assetassignments`);
      const empAssignments = getUniqueActiveAssignmentsForEmployee(response.data, employee.id);
      setSelectedEmployeeAssignments(empAssignments);
      setShowAssignmentsModal(true);
    } catch (error) {
      console.error('Error fetching assignments:', error);
      showAlert('danger', 'Failed to load assignments');
    }
  };

  const filteredEmployees = employees.filter(emp => {
    if (assetFilter === 'all') return true;
    const hasAssets = emp.assetCount > 0;
    if (assetFilter === 'with-assets') return hasAssets;
    if (assetFilter === 'without-assets') return !hasAssets;
    return true;
  }).sort((a, b) => (b.assetCount || 0) - (a.assetCount || 0));

  const columns = [
    { field: 'id', label: 'ID' },
    { field: 'name', label: 'Name' },
    { field: 'usl', label: 'USL' },
    { field: 'mobile', label: 'Mobile' },
    { field: 'department', label: 'Department' },
    { 
      field: 'assetCount', 
      label: 'Assets',
      exportValue: (value) => value || 0,
      render: (value) => (
        <span className={`badge ${value > 0 ? 'bg-success' : 'bg-secondary'}`}>
          {value || 0}
        </span>
      )
    },
    { 
      field: 'isExternal', 
      label: 'Type',
      exportValue: (value) => value ? 'External' : 'Local',
      render: (value) => (
        <span className={`badge ${value ? 'bg-info' : 'bg-secondary'}`}>
          {value ? 'External' : 'Local'}
        </span>
      )
    },
    { 
      field: 'discom', 
      label: 'Discom',
      exportValue: (value) => value?.discomName || 'N/A',
      render: (value) => value?.discomName || 'N/A'
    },
    { 
      field: 'discom', 
      label: 'Zone',
      exportValue: (value) => value?.zone || 'N/A',
      render: (value) => value?.zone || 'N/A'
    },
    { 
      field: 'discom', 
      label: 'Circle',
      exportValue: (value) => value?.circle || 'N/A',
      render: (value) => value?.circle || 'N/A'
    },
    { 
      field: 'discom', 
      label: 'Division',
      exportValue: (value) => value?.division || 'N/A',
      render: (value) => value?.division || 'N/A'
    },
    { 
      field: 'createdAt', 
      label: 'Created At',
      exportValue: (value) => new Date(value).toLocaleDateString(),
      render: (value) => new Date(value).toLocaleDateString(),
      sortValue: (value) => new Date(value).getTime()
    }
  ];

  return (
    <div className="container-fluid">
      <Alert show={alert.show} type={alert.type} message={alert.message} onClose={hideAlert} />
      
      <div className="page-header">
        <h1 className="page-title mb-0">Employees</h1>
        <div className="page-header-actions">
          <div className="btn-group" role="group">
            <input 
              type="radio" 
              className="btn-check" 
              name="assetFilter" 
              id="filterAll" 
              checked={assetFilter === 'all'}
              onChange={() => setAssetFilter('all')}
            />
            <label className="btn btn-outline-primary btn-sm" htmlFor="filterAll">
              <i className="fas fa-users d-none d-sm-inline me-1"></i>All
            </label>
            
            <input 
              type="radio" 
              className="btn-check" 
              name="assetFilter" 
              id="filterWithAssets" 
              checked={assetFilter === 'with-assets'}
              onChange={() => setAssetFilter('with-assets')}
            />
            <label className="btn btn-outline-success btn-sm" htmlFor="filterWithAssets">
              <i className="fas fa-check-circle d-none d-sm-inline me-1"></i><span className="d-none d-sm-inline">With </span>Assets
            </label>
            
            <input 
              type="radio" 
              className="btn-check" 
              name="assetFilter" 
              id="filterWithoutAssets" 
              checked={assetFilter === 'without-assets'}
              onChange={() => setAssetFilter('without-assets')}
            />
            <label className="btn btn-outline-secondary btn-sm" htmlFor="filterWithoutAssets">
              <i className="fas fa-times-circle d-none d-sm-inline me-1"></i><span className="d-none d-sm-inline">Without </span>None
            </label>
          </div>
          <button className="btn btn-outline-info btn-sm" onClick={() => setShowBulkModal(true)}>
            <i className="fas fa-upload me-1"></i><span className="d-none d-sm-inline">Bulk Operations</span><span className="d-sm-none">Bulk</span>
          </button>
          <button className="btn btn-primary btn-sm" onClick={handleAdd}>
            <i className="fas fa-plus me-1"></i><span className="d-none d-sm-inline">Add Employee</span><span className="d-sm-none">Add</span>
          </button>
        </div>
      </div>

      <div className="card border-0 shadow-sm">
        <div className="card-body">
          {loading ? (
            <div className="table-loader">
              <Loader text="Loading employees..." size="medium" />
            </div>
          ) : (
            <DataTable
              data={filteredEmployees}
              columns={columns}
              onEdit={handleEdit}
              onDelete={(id) => {
                if (typeof id === 'number' || !id.toString().startsWith('ext_')) {
                  handleDelete(id);
                }
              }}
              customActions={(item) => (
                <li>
                  <button className="dropdown-item" onClick={() => handleViewAssignments(item)}>
                    <i className="fas fa-box me-2"></i>View Assets ({item.assetCount || 0})
                  </button>
                </li>
              )}
              hideActions={(item) => item.isExternal}
            />
          )}
          {!loading && employees.length === 0 && (
            <div className="text-center py-4">
              <i className="fas fa-users fa-3x text-muted mb-3"></i>
              <p className="text-muted">No employees found. Add your first employee to get started.</p>
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      <div className={`modal fade ${showModal ? 'show d-block' : ''}`} style={{backgroundColor: showModal ? 'rgba(0,0,0,0.5)' : 'transparent'}}>
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="fas fa-users me-2"></i>
                {editingId ? 'Edit Employee' : 'Add New Employee'}
              </h5>
              <button type="button" className="btn-close" onClick={handleCloseModal}></button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Name</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter employee name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">USL</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter USL"
                    value={formData.usl}
                    onChange={(e) => setFormData({ ...formData, usl: e.target.value })}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Mobile Number</label>
                  <input
                    type="tel"
                    className="form-control"
                    placeholder="Enter mobile number"
                    value={formData.mobile}
                    onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                    pattern="[0-9]{10}"
                    title="Please enter a valid 10-digit mobile number"
                    required
                  />
                  <small className="text-muted">Enter 10-digit mobile number</small>
                </div>
                <div className="mb-3">
                  <label className="form-label">Department</label>
                  <DepartmentSelect
                    value={formData.department}
                    onChange={(value) => {
                      const actualValue = value?.target ? value.target.value : value;
                      setFormData({ ...formData, department: actualValue });
                    }}
                    required
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Discom</label>
                  <SearchableSelect
                    options={discomNames.map(name => ({ value: name, label: name }))}
                    value={formData.discomName}
                    onChange={(value) => handleDiscomChange(value)}
                    placeholder="Select Discom"
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Zone</label>
                  <SearchableSelect
                    options={zones.map(zone => ({ value: zone, label: zone }))}
                    value={formData.zone}
                    onChange={(value) => handleZoneChange(value)}
                    placeholder="Select Zone"
                    disabled={!formData.discomName}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Circle</label>
                  <SearchableSelect
                    options={circles.map(circle => ({ value: circle, label: circle }))}
                    value={formData.circle}
                    onChange={(value) => handleCircleChange(value)}
                    placeholder="Select Circle"
                    disabled={!formData.zone}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Division</label>
                  <SearchableSelect
                    options={divisions.map(division => ({ value: division, label: division }))}
                    value={formData.division}
                    onChange={(value) => {
                      const actualValue = value?.target ? value.target.value : value;
                      setFormData({ ...formData, division: actualValue });
                    }}
                    placeholder="Select Division"
                    disabled={!formData.circle}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  <i className="fas fa-save me-1"></i>
                  {editingId ? 'Update Employee' : 'Add Employee'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <div className={`modal fade ${showDeleteModal ? 'show d-block' : ''}`} style={{backgroundColor: showDeleteModal ? 'rgba(0,0,0,0.5)' : 'transparent'}}>
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="fas fa-exclamation-triangle me-2 text-warning"></i>
                Confirm Delete
              </h5>
              <button type="button" className="btn-close" onClick={() => setShowDeleteModal(false)}></button>
            </div>
            <div className="modal-body">
              Are you sure you want to delete this employee? This action cannot be undone.
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>
                Cancel
              </button>
              <button type="button" className="btn btn-danger" onClick={confirmDelete}>
                <i className="fas fa-trash me-1"></i>
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Bulk Operations Modal */}
      <EmployeeBulkOperations 
        show={showBulkModal} 
        onHide={() => setShowBulkModal(false)}
        onRefresh={fetchEmployees}
      />

      <div className={`modal fade ${showAssignmentsModal ? 'show d-block' : ''}`} style={{backgroundColor: showAssignmentsModal ? 'rgba(0,0,0,0.5)' : 'transparent'}}>
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title"><i className="fas fa-box me-2"></i>Assigned Assets</h5>
              <button type="button" className="btn-close" onClick={() => setShowAssignmentsModal(false)}></button>
            </div>
            <div className="modal-body">
              {selectedEmployeeAssignments.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>Saral Asset ID</th>
                        <th>Asset</th>
                        <th>Serial Number</th>
                        <th>Assigned Date</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedEmployeeAssignments.map((assignment) => (
                        <tr key={assignment.id}>
                          <td>{assignment.saralAssetId}</td>
                          <td>{assignment.asset?.name || 'N/A'}</td>
                          <td>{assignment.serialNumber}</td>
                          <td>{new Date(assignment.assignedDate).toLocaleDateString()}</td>
                          <td>
                            <span className={`badge ${assignment.deliveryStatus === 2 ? 'bg-success' : assignment.deliveryStatus === 1 ? 'bg-info' : 'bg-warning'}`}>
                              {assignment.deliveryStatus === 2 ? 'Delivered' : assignment.deliveryStatus === 1 ? 'In Transit' : 'Pending'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-4">
                  <i className="fas fa-inbox fa-3x text-muted mb-3"></i>
                  <p className="text-muted">No active assets assigned to this employee.</p>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowAssignmentsModal(false)}>Close</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Employees;
